import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { useAsyncState } from '@/utils/hooks';

import { LoadingIndicator, Toast, classnames, Input } from '@ygtechnologies/bygr-frontend-component-library';
import { Button, ButtonBar, Card, Checkbox } from '@ygtechnologies/rubus-ui';
import {
    createMeetingType,
    getAllInternalMeetingsTypes,
    MeetingType,
    updateMeetingType,
    updateMeetingTypes,
} from '@/services/booking';

import theme from './MeetingTypes.module.scss';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ToggleNumberInput } from './ToggleNumberInput';
import { SimplePageTitle } from '@/components/layout/PageTitle';

interface MeetingTypesProps {
    projectId?: number;
}

interface MeetingTypeExtra extends MeetingType {
    isDirty?: boolean;
    isNew?: boolean;
}

const MeetingTypes: React.FC<MeetingTypesProps> = () => {
    const project = useSelector((state: IState) => state.global.activeProject!);
    const projectId = project.id;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [canSave, setCanSave] = useState<boolean>(false);
    const [allMeetingsTypes, setAllMeetingsTypes, reloadAllMeetingsTypes] = useAsyncState<MeetingTypeExtra[] | null>(
        () => getAllInternalMeetingsTypes(projectId),
        [projectId],
        null
    );

    useEffect(() => {
        if (allMeetingsTypes) {
            setIsLoading(false);
        }
    }, [allMeetingsTypes]);

    const handleMeetingTypeChange = (meetingType: MeetingTypeExtra, changes: Partial<MeetingTypeExtra>) => {
        setCanSave(true);
        if (allMeetingsTypes) {
            const newArray = arrayReplace(allMeetingsTypes, meetingType, { ...meetingType, ...changes, isDirty: true });
            setAllMeetingsTypes(newArray);
        }
    };

    const handleSaveTypes = async (meetingType: MeetingTypeExtra) => {
        if (meetingType.isNew) {
            await createMeetingType(projectId, meetingType)
                .then(() => {
                    Toast.success(<Trans>Successfully created a new meeting type</Trans>);
                    meetingType.isNew = false;
                })
                .catch((e) => Toast.error(<Trans>Something went wrong.</Trans>));
        } else {
            await updateMeetingType(projectId, meetingType.id!, meetingType)
                .then(() => Toast.success(<Trans>Successfully updated meeting type</Trans>))
                .catch((e) => Toast.error(<Trans>Something went wrong.</Trans>));
        }
        const newArray = arrayReplace(allMeetingsTypes!, meetingType, { ...meetingType, isDirty: false });

        setAllMeetingsTypes(newArray);
        await reloadAllMeetingsTypes();
    };

    const handleNewTypes = () => {
        setCanSave(true);
        if (allMeetingsTypes) {
            const newArray = allMeetingsTypes.concat([
                { needsConfirmation: false, name: '', isNew: true, daysBeforeLockConfirmation: 0, daysBeforeReminder: 0 },
            ]);
            setAllMeetingsTypes(newArray);
        }
    };
    const handleSaveAll = async () => {
        let toBeUpdated: MeetingTypeExtra[] = [];
        let toBeAdded: MeetingTypeExtra[] = [];
        if (allMeetingsTypes) {
            allMeetingsTypes.forEach((x) => {
                if (x.isNew) {
                    toBeAdded.push(x);
                } else if (x.isDirty) {
                    toBeUpdated.push(x);
                }
            });
        }
        let errorInAdded = false;
        for (const x of toBeAdded) {
            await createMeetingType(projectId, x)
                .then(() => {
                    x.isNew = false;
                })
                .catch((e) => {
                    errorInAdded = true;
                });
        }
        if (toBeUpdated.length === 0) {
            if (!errorInAdded) {
                Toast.success(<Trans>Successfully created a new meeting type</Trans>);
            } else {
                Toast.error(<Trans>Something went wrong.</Trans>);
            }
        }
        if (toBeUpdated.length > 0) {
            await updateMeetingTypes(projectId, toBeUpdated)
                .then(() => {
                    if (errorInAdded) {
                        Toast.error(<Trans>Something went wrong.</Trans>);
                    } else {
                        Toast.success(<Trans>Successfully updated meeting types</Trans>);
                    }
                })
                .catch((e) => Toast.error(<Trans>Something went wrong.</Trans>));
        }
        await reloadAllMeetingsTypes();
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    return (
        <Card>
            <SimplePageTitle title={t`Meetings`} />
            <ButtonBar className={theme.meetingTypeHeader}>
                <div className={theme.meetingTypeTitle}>
                    <Trans>Meeting type</Trans>
                </div>
                <ButtonBar.Right>
                    <Button onClick={handleNewTypes} variant="primary">
                        <Trans>New meeting type</Trans>
                    </Button>
                    <Button disabled={!canSave} onClick={handleSaveAll} variant="primary">
                        <Trans>Save</Trans>
                    </Button>
                </ButtonBar.Right>
            </ButtonBar>
            <div className={theme.meetingTypeContent}>
                {allMeetingsTypes?.length === 0 ? (
                    <div className={classnames(theme.noMeetings)}>
                        <Trans>Create a new meeting type to start</Trans>
                    </div>
                ) : (
                    <table className={theme.rubusTable}>
                        <thead>
                            <tr>
                                <th>
                                    <Trans>Name</Trans>
                                </th>
                                <th className={classnames(theme.meetingRowCheck, theme.meetingRowCentered)}>
                                    <Trans>Needs confirmation</Trans>
                                </th>
                                <th className={theme.meetingRowCheck}>
                                    <Trans>Lock confirming x-days before</Trans>
                                </th>
                                <th className={theme.meetingRowCheck}>
                                    <Trans>Send reminder x-days before</Trans>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {allMeetingsTypes!.map((x, i) => (
                                <tr key={i} className={theme.meetingRow}>
                                    <td className={theme.meetingRowName}>
                                        <Input
                                            value={x.name}
                                            onChange={(event) => handleMeetingTypeChange(x, { name: event.target.value })}
                                            className={classnames('form-control')}
                                        />
                                    </td>
                                    <td className={classnames(theme.meetingRowCheck, theme.meetingRowCentered)}>
                                        <Checkbox
                                            inputSize="md"
                                            name="needsConfirmation"
                                            checked={x.needsConfirmation}
                                            onChange={(e) =>
                                                handleMeetingTypeChange(x, { needsConfirmation: e.target.checked })
                                            }
                                        />
                                    </td>

                                    <td className={theme.meetingRowCheck}>
                                        <ToggleNumberInput
                                            disabled={!x.needsConfirmation}
                                            onChange={(newValue) =>
                                                handleMeetingTypeChange(x, { daysBeforeLockConfirmation: newValue })
                                            }
                                            value={x.daysBeforeLockConfirmation}
                                        />
                                    </td>

                                    <td className={theme.meetingRowCheck}>
                                        <ToggleNumberInput
                                            onChange={(newValue) =>
                                                handleMeetingTypeChange(x, { daysBeforeReminder: newValue })
                                            }
                                            value={x.daysBeforeReminder}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </Card>
    );
};

export default MeetingTypes;
